import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import {
    Star, CheckCircle, XCircle, LinkSimple, Copy, PaperPlaneTilt,
    ClockCounterClockwise, ClipboardText, PencilSimple,
} from "@phosphor-icons/react";

/**
 * InterviewAssessmentPanel — V1.5.7.
 * Renders inline under each interview in the candidate drawer. Recruiter can:
 *   • Fill the assessment themselves
 *   • Generate a magic link (copy or email to interviewer) — auto-picks the
 *     interviewer email from `interview.attendees` if present, else lets the
 *     recruiter type one.
 *   • View the submitted assessment in a compact read-only summary.
 */
export default function InterviewAssessmentPanel({ candidateId, interview, onUpdated }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState("view");  // view | edit | share
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [skillRatings, setSkillRatings] = useState({});
    const [generic, setGeneric] = useState({});
    const [notes, setNotes] = useState("");
    const [status, setStatus] = useState("");
    const [shareUrl, setShareUrl] = useState("");
    const [emailInput, setEmailInput] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(
                `/candidates/${candidateId}/interviews/${interview.id}/assessment`,
            );
            setData(r.data);
            if (r.data.assessment) {
                setSkillRatings(r.data.assessment.skill_ratings || {});
                setGeneric(r.data.assessment.generic || {});
                setNotes(r.data.assessment.notes || "");
                setStatus(r.data.assessment.status || "");
            }
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setLoading(false);
        }
    }, [candidateId, interview.id]);
    useEffect(() => { load(); }, [load]);

    const canSubmit = useMemo(() => {
        if (!status) return false;
        return (data?.generic_questions || []).every((q) => generic[q.key]);
    }, [status, generic, data]);

    const save = async () => {
        if (!canSubmit) return;
        setSaving(true);
        setErr("");
        try {
            await api.post(
                `/candidates/${candidateId}/interviews/${interview.id}/assessment`,
                {
                    skill_ratings: Object.entries(skillRatings).map(([skill, rating]) => ({ skill, rating })),
                    generic, status, notes,
                },
            );
            toast.success(
                status === "rejected"
                    ? "Assessment saved — candidate moved to Rejected"
                    : "Assessment saved — candidate marked as Selected",
            );
            setMode("view");
            await load();
            onUpdated?.();
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setSaving(false);
        }
    };

    const share = async ({ sendEmail = false } = {}) => {
        setSaving(true);
        setErr("");
        try {
            const payload = { send_email: sendEmail };
            if (emailInput) payload.email = emailInput;
            const r = await api.post(
                `/candidates/${candidateId}/interviews/${interview.id}/assessment/share`,
                payload,
            );
            setShareUrl(r.data.url);
            if (sendEmail && r.data.email_sent) {
                toast.success(`Email sent to ${r.data.to_email}`);
            } else if (sendEmail && r.data.email_error) {
                toast.warning("Link generated — email failed", { description: r.data.email_error });
            } else {
                toast.success("Assessment link generated — copy or send.");
            }
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setSaving(false);
        }
    };

    const copyLink = async () => {
        try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
        catch { toast.error("Copy failed"); }
    };

    if (loading) {
        return (
            <div data-testid="assessment-loading"
                 className="mt-3 pt-3 border-t border-[#F5F5F5] text-[10px] text-[#A3A3A3]">
                Loading assessment…
            </div>
        );
    }

    const assessment = data?.assessment;
    const skills = data?.skills || [];
    const genericQs = data?.generic_questions || [];
    const stagedForRejected = assessment?.status === "rejected";

    return (
        <div
            data-testid={`assessment-panel-${interview.id}`}
            className="mt-3 pt-3 border-t border-[#F5F5F5] space-y-2"
        >
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-cobalt">
                    <ClipboardText size={11} weight="fill" /> Interview assessment
                </div>
                {assessment && mode === "view" && (
                    <button
                        onClick={() => setMode("edit")}
                        data-testid={`assessment-edit-btn-${interview.id}`}
                        className="text-[10px] font-bold text-[#525252] hover:text-cobalt inline-flex items-center gap-1"
                    >
                        <PencilSimple size={10} weight="bold" /> Edit
                    </button>
                )}
            </div>
            {err && (
                <div className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-xs px-3 py-2">
                    {err}
                </div>
            )}

            {/* Empty state — 2-CTA */}
            {!assessment && mode === "view" && !shareUrl && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                        onClick={() => setMode("edit")}
                        data-testid={`assessment-fill-self-${interview.id}`}
                        className="border border-[#E5E5E5] hover:border-cobalt bg-[#F9FBFF] p-3 text-left transition-colors"
                    >
                        <p className="text-[10px] font-black uppercase tracking-[0.15em] text-cobalt mb-0.5">
                            Fill it yourself
                        </p>
                        <p className="text-[11px] text-[#525252]">Rate the interview inline — takes ~1 min.</p>
                    </button>
                    <button
                        onClick={() => setMode("share")}
                        data-testid={`assessment-share-open-${interview.id}`}
                        className="border border-[#E5E5E5] hover:border-cobalt bg-white p-3 text-left transition-colors"
                    >
                        <p className="text-[10px] font-black uppercase tracking-[0.15em] text-ink mb-0.5">
                            Send to interviewer
                        </p>
                        <p className="text-[11px] text-[#525252]">Magic link — copy or email. Single-use.</p>
                    </button>
                </div>
            )}

            {/* Share workflow */}
            {mode === "share" && !shareUrl && (
                <div className="bg-[#F9FBFF] border border-cobalt/30 p-3 space-y-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-cobalt">
                        Interviewer email (optional)
                    </p>
                    <input
                        data-testid={`assessment-share-email-${interview.id}`}
                        type="email"
                        placeholder="interviewer@company.com"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        className="w-full bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                    />
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => share({ sendEmail: false })}
                            disabled={saving}
                            data-testid={`assessment-share-copy-${interview.id}`}
                            className="bg-ink text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-60"
                        >
                            <LinkSimple size={11} weight="bold" /> Generate link only
                        </button>
                        <button
                            onClick={() => share({ sendEmail: true })}
                            disabled={saving || !emailInput}
                            data-testid={`assessment-share-email-btn-${interview.id}`}
                            className="bg-cobalt text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-60"
                        >
                            <PaperPlaneTilt size={11} weight="bold" /> Email link
                        </button>
                        <button
                            onClick={() => setMode("view")}
                            className="text-[10px] font-bold uppercase tracking-wider text-[#525252] hover:text-ink px-2 py-1"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Generated link */}
            {shareUrl && (
                <div className="border border-cobalt/40 bg-[#F9FBFF] p-3 space-y-2">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-cobalt">
                        <LinkSimple size={11} weight="bold" /> Link ready
                    </div>
                    <div className="flex gap-2">
                        <input
                            readOnly value={shareUrl}
                            className="flex-1 min-w-0 bg-white border border-[#E5E5E5] px-3 py-1.5 text-xs"
                        />
                        <button
                            onClick={copyLink}
                            data-testid={`assessment-copy-${interview.id}`}
                            className="bg-cobalt text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1"
                        >
                            <Copy size={11} weight="bold" /> Copy
                        </button>
                    </div>
                </div>
            )}

            {/* Filled state */}
            {assessment && mode === "view" && (
                <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-3 space-y-2">
                    <div className="flex items-center gap-2">
                        {stagedForRejected ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-white bg-[#DC2626] px-2 py-0.5">
                                <XCircle size={10} weight="fill" /> Rejected
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-white bg-emerald-600 px-2 py-0.5">
                                <CheckCircle size={10} weight="fill" /> Selected
                            </span>
                        )}
                        <span className="text-[10px] text-[#A3A3A3]">
                            by {assessment.submitted_by} · {new Date(assessment.submitted_at).toLocaleString()}
                        </span>
                    </div>
                    {Object.keys(assessment.skill_ratings || {}).length > 0 && (
                        <div>
                            <p className="text-[9px] font-black uppercase text-[#A3A3A3] mb-1">Skill ratings</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                                {Object.entries(assessment.skill_ratings).map(([s, r]) => (
                                    <p key={s} className="text-xs text-ink"><strong>{s}:</strong> {r}/5</p>
                                ))}
                            </div>
                        </div>
                    )}
                    <div>
                        <p className="text-[9px] font-black uppercase text-[#A3A3A3] mb-1">Evaluation</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1">
                            {genericQs.map((q) => (
                                <p key={q.key} className="text-xs text-ink">
                                    <strong>{q.label}:</strong> {assessment.generic?.[q.key] ?? "—"}/5
                                </p>
                            ))}
                        </div>
                    </div>
                    {assessment.notes && (
                        <p className="text-xs text-[#525252] italic">"{assessment.notes}"</p>
                    )}
                </div>
            )}

            {/* Edit / Fill mode */}
            {mode === "edit" && (
                <div className="bg-white border border-cobalt/40 p-3 space-y-3">
                    {skills.length > 0 && (
                        <div>
                            <p className="text-[9px] font-black uppercase text-[#525252] mb-1.5">Skill ratings</p>
                            <div className="space-y-1.5">
                                {skills.map((s) => (
                                    <MiniStarRow
                                        key={s} label={s}
                                        value={skillRatings[s] || 0}
                                        onChange={(v) => setSkillRatings((prev) => ({ ...prev, [s]: v }))}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    <div>
                        <p className="text-[9px] font-black uppercase text-[#525252] mb-1.5">
                            Evaluation <span className="text-[#DC2626]">*</span>
                        </p>
                        <div className="space-y-1.5">
                            {genericQs.map((q) => (
                                <MiniStarRow
                                    key={q.key} label={q.label}
                                    value={generic[q.key] || 0}
                                    onChange={(v) => setGeneric((prev) => ({ ...prev, [q.key]: v }))}
                                />
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="block text-[9px] font-black uppercase text-[#525252] mb-1">Notes</label>
                        <textarea
                            data-testid={`assessment-notes-${interview.id}`}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            className="w-full bg-white border border-[#E5E5E5] px-3 py-2 text-xs focus:outline-none focus:border-cobalt"
                        />
                    </div>
                    <div>
                        <p className="text-[9px] font-black uppercase text-[#525252] mb-1.5">
                            Verdict <span className="text-[#DC2626]">*</span>
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                data-testid={`assessment-status-selected-${interview.id}`}
                                onClick={() => setStatus("selected")}
                                className={`py-2 text-[10px] font-black uppercase tracking-wider border-2 transition-colors ${
                                    status === "selected"
                                        ? "bg-emerald-600 border-emerald-600 text-white"
                                        : "bg-white border-[#E5E5E5] text-[#525252]"
                                }`}
                            >
                                ✓ Selected
                            </button>
                            <button
                                type="button"
                                data-testid={`assessment-status-rejected-${interview.id}`}
                                onClick={() => setStatus("rejected")}
                                className={`py-2 text-[10px] font-black uppercase tracking-wider border-2 transition-colors ${
                                    status === "rejected"
                                        ? "bg-[#DC2626] border-[#DC2626] text-white"
                                        : "bg-white border-[#E5E5E5] text-[#525252]"
                                }`}
                            >
                                ✗ Rejected
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-1">
                        {assessment && (
                            <button
                                onClick={() => { setMode("view"); load(); }}
                                className="text-[10px] font-bold uppercase tracking-wider text-[#525252] hover:text-ink px-2 py-1"
                            >
                                Cancel
                            </button>
                        )}
                        <button
                            onClick={save}
                            disabled={saving || !canSubmit}
                            data-testid={`assessment-save-${interview.id}`}
                            className="bg-cobalt hover:bg-cobalt-dark disabled:opacity-60 text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1"
                        >
                            <CheckCircle size={10} weight="fill" />
                            {saving ? "Saving…" : "Save assessment"}
                        </button>
                    </div>
                </div>
            )}

            {data?.pending_share && !shareUrl && !assessment && mode === "view" && (
                <div className="border border-amber-200 bg-amber-50 text-amber-900 p-2 flex items-center gap-2 text-[10px]">
                    <ClockCounterClockwise size={11} weight="fill" />
                    <span className="flex-1">Waiting for interviewer to submit the shared link.</span>
                </div>
            )}
        </div>
    );
}

function MiniStarRow({ label, value, onChange }) {
    return (
        <div className="flex items-center justify-between gap-2 text-xs">
            <p className="font-bold text-ink flex-1 min-w-0 break-words">{label}</p>
            <div className="flex items-center gap-0.5 shrink-0">
                {[1, 2, 3, 4, 5].map((n) => (
                    <button
                        key={n}
                        type="button"
                        onClick={() => onChange(n)}
                        className={n <= value ? "text-[#F59E0B]" : "text-[#E5E5E5] hover:text-[#A3A3A3]"}
                        aria-label={`Rate ${n}`}
                    >
                        <Star size={14} weight="fill" />
                    </button>
                ))}
            </div>
        </div>
    );
}
